import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Package, Truck, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandLogo } from "../components/BrandLogo";
import { OrderProgress } from "../components/OrderProgress";
import ZellePayBlock from "../components/ZellePayBlock";
import { api, money } from "../lib/api";
import { useT } from "../lib/i18n";

export default function TrackOrder() {
  const { t } = useT();
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/orders/track/${token}`);
      setData(r.data);
      setState("ok");
    } catch {
      setState("error");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-dvh hero-wash">
      <div className="mx-auto max-w-lg px-4 py-10">
        <BrandLogo size={40} withText />
        <div className="mt-6 rounded-[1.2rem] border bg-card p-6 shadow-[var(--shadow-soft)]">
          {state === "loading" && (
            <div className="flex flex-col items-center py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "hsl(var(--brand-rose))" }} />
              <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Loading your order...</p>
            </div>
          )}
          {state === "error" && (
            <div className="py-6 text-center">
              <h1 className="font-display text-xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Order not found</h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">This tracking link is invalid or has expired.</p>
            </div>
          )}
          {state === "ok" && data && (
            <div data-testid="track-order">
              <div className="flex items-center gap-3">
                <Package className="h-8 w-8" style={{ color: "hsl(var(--brand-rose))" }} />
                <div>
                  <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Hi {data.first_name}!</h1>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Order <b data-testid="track-order-number">{data.order_number}</b></p>
                </div>
              </div>

              <dl className="mt-5 space-y-1.5 rounded-xl border bg-[hsl(var(--muted))] p-4 text-sm">
                <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Magnets</dt><dd>{data.kit_qty}</dd></div>
                {data.gift_packages > 0 && <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("gift_packaging")}</dt><dd>{data.gift_packages}</dd></div>}
                <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Total</dt><dd className="font-semibold">{money(data.total_cents)}</dd></div>
              </dl>

              <div className="mt-5">
                <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Order status</div>
                <OrderProgress steps={data.steps} />
              </div>

              {data.zelle && (
                <div className="mt-5" data-testid="track-zelle">
                  <ZellePayBlock zelle={data.zelle} amountCents={data.total_cents} orderNumber={data.order_number} />
                </div>
              )}

              {data.shipping && (
                <div className="mt-5 rounded-xl border p-4" data-testid="track-shipping">
                  <div className="text-sm font-semibold">Shipping</div>
                  <div className="mt-1 text-sm">Carrier: <b>{data.shipping.carrier}</b></div>
                  <div className="text-sm">Tracking: <b>{data.shipping.tracking_number}</b></div>
                  <a href={data.shipping.tracking_url} target="_blank" rel="noopener noreferrer">
                    <Button className="mt-3 h-11 w-full rounded-xl" data-testid="track-package-button"><Truck className="mr-1.5 h-4 w-4" /> Track package</Button>
                  </a>
                </div>
              )}

              <p className="mt-5 flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]"><ShieldCheck className="h-3.5 w-3.5" /> Updates are emailed to the address you used at checkout.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
